import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";

const STEPS = [
  {
    t: "Choose a real-world situation.",
    d: "Seven scenario cards cover the most common emergencies and safety questions.",
    highlight: "hero",
  },
  {
    t: "LIFEGRID converts uncertainty into actions.",
    d: "Every choice unlocks a Right Now / Next / Share flow. No essays. No jargon.",
    highlight: "instant",
  },
  {
    t: "Share your status.",
    d: "One tap generates a shareable link your Trust Circle can open instantly.",
    highlight: "share",
  },
  {
    t: "Use accessibility and voice.",
    d: "Toggle large mode, high contrast or read-aloud voice — designed for high-stress use.",
    highlight: "a11y",
  },
  {
    t: "Imagine the LIFEGRID network.",
    d: "A future ecosystem of trust circles, community and services — clearly labelled as vision, not fact.",
    highlight: "network",
  },
];

export function JudgeMode() {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  const step = STEPS[i];

  return (
    <>
      <button
        className="btn-tactile primary fixed bottom-6 right-6 z-30 shadow-xl"
        onClick={() => {
          setI(0);
          setOpen(true);
        }}
        data-testid="judge-mode-open"
      >
        <Sparkles size={14} /> Judge Mode
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-center justify-center"
            data-testid="judge-mode-overlay"
          >
            <motion.div
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 16, opacity: 0 }}
              className="glass rounded-md w-full max-w-lg p-6"
            >
              <div className="flex items-center justify-between">
                <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">
                  JUDGE MODE · GUIDED TOUR
                </div>
                <button className="btn-tactile ghost !p-2" onClick={() => setOpen(false)} data-testid="judge-close">
                  <X size={14} />
                </button>
              </div>
              <div className="mt-4">
                <div className="flex gap-1 mb-4">
                  {STEPS.map((_, idx) => (
                    <span
                      key={idx}
                      className={`h-1 flex-1 rounded-full ${idx <= i ? "bg-[color:var(--lg-lime)]" : "bg-white/10"}`}
                    />
                  ))}
                </div>
                <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-lime)]">
                  STEP {String(i + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}
                </div>
                <h3 className="mt-2 font-display text-2xl font-extrabold uppercase">{step.t}</h3>
                <p className="mt-2 text-[color:var(--lg-text-2)]">{step.d}</p>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <button
                  className="btn-tactile ghost"
                  onClick={() => setI(Math.max(0, i - 1))}
                  disabled={i === 0}
                  data-testid="judge-prev"
                >
                  <ChevronLeft size={14} /> Back
                </button>
                {i < STEPS.length - 1 ? (
                  <button className="btn-tactile primary" onClick={() => setI(i + 1)} data-testid="judge-next">
                    Next <ChevronRight size={14} />
                  </button>
                ) : (
                  <button className="btn-tactile primary" onClick={() => setOpen(false)} data-testid="judge-finish">
                    Finish
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
